import { Component, ElementRef, Input, OnChanges } from '@angular/core';
import { DropdownService } from 'src/app/project/components/dropdown/dropdown-service/dropdown.service';
import { TDropdown } from 'src/app/project/components/dropdown/dropdown.consts';
import { PermissionsService } from 'src/app/project/modules/share/permissions.service';
import { canEditPoint } from 'src/app/project/modules/share/share-utils/share-permissions';
import { EUserRole } from 'src/app/project/modules/share/share-utils/user-roles';
import { TWorkspace } from 'src/app/project/modules/workspace/workspace.model';
import { WorkspaceService } from 'src/app/project/modules/workspace/workspace.service';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { PointsService } from '../../../points.service';
import { PointActionBarOptionsDropdownComponent } from '../../point-action-bar/point-action-bar-options-dropdown/point-action-bar-options-dropdown.component';
import { iconMap } from 'src/app/project/shared/enums/icon-map';

@Component({
  selector: 'pp-point-options',
  templateUrl: './point-options.component.html',
  styleUrls: ['./point-options.component.scss'],
  standalone: false,
})
export class PointOptionsComponent implements OnChanges {
  @Input() ppPointId: string;
  @Input() ppFull: boolean;
  @Input() ppNew: boolean;
  @Input() ppWorkspaceId: string;

  EIconPath = EIconPath;
  iconMap = iconMap;
  buttonVisible: boolean;
  buttonId = 'pointOptions';

  private canExport: boolean;
  private canCopy: boolean;
  private canMove: boolean;
  private canDelete: boolean;
  private canEditCustomFields: boolean;
  private canEditTags: boolean;

  private dropdown: TDropdown = this.dropdownService.getDropdown();

  constructor(
    private permissionsService: PermissionsService,
    private dropdownService: DropdownService,
    private workspaceService: WorkspaceService,
    private elementRef: ElementRef,
    private pointsService: PointsService,
  ) {}

  ngOnChanges(): void {
    this.initPermissions();
  }

  showMoreOptions(): void {
    if (this.dropdown.visible && this.dropdown.buttonId === this.buttonId) {
      this.dropdownService.hideDropdown();
    } else {
      this.setDropdownData();

      this.dropdownService.showDropdown(this.buttonId, PointActionBarOptionsDropdownComponent, {});
    }
  }

  private initPermissions(): void {
    const workspaces = this.workspaceService.getWorkspaces();

    const workspace = this.ppWorkspaceId ? workspaces[this.ppWorkspaceId] : null;

    if (workspace === null) {
      this.canEditTags = false;
      this.canEditCustomFields = false;
    } else if (workspace) {
      this.setTagPermission();
      this.setCustomFieldPermission(workspace);
    }

    this.setExportPermission();
    this.setCopyPermission(workspace);
    this.setMovePermission(workspace);
    this.setDeletePermission(workspace);
    this.setButtonVisibility();
  }

  private setTagPermission(): void {
    this.canEditTags = this.permissionsService.getTagPermissions(this.ppWorkspaceId).edit;
  }

  private setCustomFieldPermission(workspace: TWorkspace): void {
    const customFieldPermissions = this.permissionsService.checkWorkspaceCustomFieldPermissions(
      this.ppWorkspaceId,
    );

    if (Object.keys(customFieldPermissions).length > 0) {
      Object.keys(customFieldPermissions).forEach((customFieldId) => {
        if (customFieldPermissions[customFieldId].edit) {
          this.canEditCustomFields = true;
        }
      });
    } else {
      this.canEditCustomFields = canEditPoint(workspace.share.shareOption);
    }
  }

  private setDeletePermission(workspace: TWorkspace): void {
    this.canDelete = workspace && this.hasAdminPermissions(workspace.share.shareOption);
  }

  private setMovePermission(workspace: TWorkspace): void {
    this.canMove = workspace && this.hasAdminPermissions(workspace.share.shareOption);
  }

  private setCopyPermission(workspace: TWorkspace): void {
    this.canCopy =
      workspace &&
      (this.hasAdminPermissions(workspace.share.shareOption) ||
        (workspace.share.shareOption === EUserRole.NORMAL &&
          this.canEditCustomFields &&
          this.canEditTags));
  }

  private setExportPermission(): void {
    this.canExport = this.ppWorkspaceId
      ? this.permissionsService.areExportsEnabled(this.ppWorkspaceId)
      : false;
  }

  private setDropdownData(): void {
    this.dropdownService.setData({
      point: this.pointsService.findPoint(this.ppPointId),
      isFull: this.ppFull,
      canExport: this.canExport,
      canMove: this.canMove,
      canCopy: this.canCopy,
      canDelete: this.canDelete,
    });
  }

  private setButtonVisibility(): void {
    const point = this.pointsService.findPoint(this.ppPointId);

    this.buttonVisible =
      !this.ppNew && point && (this.canCopy || this.canMove || this.canExport || this.canDelete);
    const host = this.elementRef.nativeElement;

    host.style.display = this.buttonVisible ? 'block' : 'none';
  }

  private hasAdminPermissions(shareOption: string): boolean {
    return (
      shareOption === EUserRole.SITE_ADMIN ||
      shareOption === EUserRole.ACCOUNT_ADMIN ||
      shareOption === EUserRole.OWNER
    );
  }
}
